/* The "compete" pages: four static money pages that go after the search terms
   UK buyers use when they are about to hire a venue finder, a corporate event
   planner or an event management company, and offer Comtev's AI venue finder
   and planning agent as the faster answer (seo/strategy-2026-10-13.md, round 2).

   One renderer (src/pages/CompetePage.tsx), four records here. Every string is
   a marketing claim; the owner directed on 2026-10-13 that the venue finder is
   marketed as requesting availability, handling the venue comms and confirming
   the booking on the buyer's behalf (see marketing-claims-guardrails). Pricing
   follows the plan catalogue (free to start, plans from £79 a month). Flights
   and hotel rooms are the only things booked and charged in-app, after approval. */

export type CompeteStat = { value: string; label: string };
export type CompeteStep = { title: string; body: string };
export type CompeteRow = { topic: string; them: string; us: string };
export type CompeteFeature = { title: string; body: string };
export type CompeteFaq = { q: string; a: string };
export type CompeteLink = { to: string; label: string };

export type CompetePage = {
  slug: string;
  path: string;
  title: string;
  description: string;
  eyebrow: string;
  h1: string;
  sub: string;
  heroNote: string;
  stats: CompeteStat[];
  /** The first paragraph after the hero: the lift-able answer to the query. */
  answer: string;
  stepsTitle: string;
  steps: CompeteStep[];
  tableTitle: string;
  tableHead: [string, string];
  rows: CompeteRow[];
  tableNote: string;
  featuresTitle: string;
  features: CompeteFeature[];
  faq: CompeteFaq[];
  related: CompeteLink[];
  ctaTitle: string;
  ctaBody: string;
  breadcrumb: string;
  serviceName: string;
};

export const COMPETE_PAGES: CompetePage[] = [
  {
    slug: "venue-finder",
    path: "/venue-finder",
    title: "AI Venue Finder for UK Corporate Events | Comtev",
    description:
      "Comtev's AI venue finder returns a ranked shortlist in minutes, requests availability, handles the venue comms and confirms the booking for you. Free to start, plans from £79 a month, no venue commission.",
    eyebrow: "AI venue finder",
    h1: "The venue finder that actually finds the venue.",
    sub: "Tell Comtev the date, the headcount, the city and the budget. Within minutes it has a ranked shortlist, has asked every venue on it whether your date is free, and is handling the replies. You pick. It confirms the booking.",
    heroNote: "Free to start, plans from £79 a month. Venues pay Comtev nothing, so the shortlist is yours, not theirs.",
    stats: [
      { value: "Minutes", label: "from a typed brief to a ranked shortlist" },
      { value: "Same hour", label: "availability requested from every venue on the list" },
      { value: "4 channels", label: "WhatsApp, SMS, email and voice, so venues answer where they actually answer" },
      { value: "£0", label: "commission from venues. Ever." },
    ],
    answer:
      "An AI venue finder reads your event brief, searches the market for venues that fit, and then does the work a venue-finding agency charges for: it requests availability, handles the back-and-forth, negotiates the rate and confirms the booking. Comtev does this for UK corporate events on plans that start free, and every hold and quote lands in one live budget that a named approver signs off.",
    stepsTitle: "Brief to booked, in four steps you mostly watch.",
    steps: [
      {
        title: "Write the brief. Badly, if you like.",
        body: "A date or a range, a headcount, a city, the feel you want and the budget you have. Thirty seconds. Comtev fills the gaps with questions, not assumptions.",
      },
      {
        title: "The shortlist arrives ranked.",
        body: "Capacity fit, location, layout, what each venue is known for and the watch-outs. Ordered by fit to your brief, not by who paid to be at the top.",
      },
      {
        title: "Availability is requested while you read.",
        body: "Comtev contacts every venue on the shortlist over WhatsApp, SMS, email or a phone call, asks for your dates, chases the ones that go quiet and reads the replies back into the list.",
      },
      {
        title: "You choose. Comtev confirms.",
        body: "Pick the venue. Comtev negotiates the rate, agrees the terms, confirms the booking with the venue on your behalf and drops the cost into the event budget for sign-off.",
      },
    ],
    tableTitle: "A venue-finding agency, and the same job done by software.",
    tableHead: ["Venue-finding agency", "Comtev"],
    rows: [
      { topic: "Time to a shortlist", them: "A call first, then two to five working days", us: "Minutes, from a typed brief" },
      { topic: "Who the venue pays", them: "The agency, through commission on your booking", us: "Nobody. Venues pay Comtev nothing" },
      { topic: "Availability checks", them: "Emails from a coordinator, chased when remembered", us: "Every venue asked the same hour, chased automatically" },
      { topic: "Comms with the venue", them: "In the agency's inbox", us: "Handled by Comtev, every message logged on the event" },
      { topic: "Negotiation", them: "Depends on the account manager", us: "Comtev pushes on rate and terms and shows you the result" },
      { topic: "Booking", them: "The agency confirms, you sign", us: "Comtev confirms with the venue; spend waits for your named approver" },
      { topic: "What it costs you", them: "Built into the venue's price", us: "A plan from £79 a month, free to start, whatever the venue costs" },
    ],
    tableNote:
      "Venue platforms such as Hire Space, HeadBox and Tagvenue publish venue-side pricing on their own sites, as commission on bookings or a listing subscription. The venue builds it into the price you pay. Figures vary, so ask whoever you use.",
    featuresTitle: "What you get that a catalogue cannot give you.",
    features: [
      {
        title: "Any venue, not a catalogue",
        body: "Comtev searches the open market, so a members' club, a university hall or a brewery taproom can make the list. You are not shopping inside someone else's inventory.",
      },
      {
        title: "Decision axes, not brochures",
        body: "Each venue comes with capacity fit, travel time for your guests, layout notes and the things that tend to go wrong there, written for the person who has to decide.",
      },
      {
        title: "Holds and quotes land in one budget",
        body: "Every hold, quote and negotiated rate sits in the event budget next to catering, AV and travel. Try a scenario, then share a frozen proposal.",
      },
      {
        title: "Nothing is charged without a yes",
        body: "Spending routes to a named approver with a ceiling, on Slack, push or email. The approval history stays with the event.",
      },
      {
        title: "Flights and rooms from the same brief",
        body: "If people are travelling, Comtev prices flights per named traveller and compares room blocks, and books both in-app once approved.",
      },
    ],
    faq: [
      {
        q: "How fast is the AI venue finder, honestly?",
        a: "A ranked shortlist is usually ready within minutes of the brief. Availability depends on venues answering, so Comtev asks them all at once over the channel they actually use and chases the quiet ones. You normally have dates confirmed in hours rather than the days an email round takes.",
      },
      {
        q: "Does Comtev book the venue, or just find it?",
        a: "Both. Once you choose, Comtev negotiates the rate and terms, confirms the booking with the venue on your behalf and records it on the event. The venue's contract and invoice stay between you and the venue. Flights and hotel rooms are booked in-app after approval.",
      },
      {
        q: "Is the shortlist biased towards venues that pay you?",
        a: "No. Venues pay Comtev nothing. You pay for a plan, so the ranking has exactly one customer, and it is you.",
      },
      {
        q: "Can it handle London, Manchester and Edinburgh in the same brief?",
        a: "Yes. Give it one city or several and it searches and requests availability in each, then shows the options side by side with travel costs from your attendees' home cities.",
      },
      {
        q: "What if I already have a venue in mind?",
        a: "Add it. Comtev will request availability, negotiate and confirm it the same way, and still show you two or three alternatives so you can see whether the price is fair.",
      },
      {
        q: "Who is this for?",
        a: "In-house event teams, executive assistants, operations and people teams at UK companies. Agencies and freelance planners use it for client events too; the budget and the proposal can be shared with the client.",
      },
    ],
    related: [
      { to: "/venue-finder/london", label: "Venue finder for London" },
      { to: "/corporate-event-planner", label: "The AI corporate event planner" },
      { to: "/event-management-company", label: "Instead of an event management company" },
      { to: "/products/quotes", label: "Supplier quotes and negotiation" },
      { to: "/corporate-event-cost-uk", label: "What UK corporate events cost" },
      { to: "/pricing", label: "Pricing" },
    ],
    ctaTitle: "Bring a venue brief to the demo.",
    ctaBody: "A date, a headcount and a city is enough. We will run it live, and you will leave with a real shortlist.",
    breadcrumb: "AI venue finder",
    serviceName: "AI venue finding and booking for corporate events",
  },
  {
    slug: "venue-finder-london",
    path: "/venue-finder/london",
    title: "Venue Finder London: Corporate Venues Found by AI | Comtev",
    description:
      "Finding a London venue for a corporate event? Comtev's AI shortlists venues across the city in minutes, checks availability, negotiates and confirms the booking. Free to start, plans from £79 a month.",
    eyebrow: "Venue finder, London",
    h1: "London venues, found and held before the meeting ends.",
    sub: "London has more corporate venues than anyone can hold in their head, and most of them answer email slowly. Comtev searches the whole city against your brief, asks every venue on the shortlist for your dates and handles the replies. A conference room in the City, a Christmas party in Shoreditch or an all-hands in King's Cross gets shortlisted, priced and held in the time it used to take to write the enquiry.",
    heroNote: "Free to start, plans from £79 a month, priced in pounds. No commission from any London venue.",
    stats: [
      { value: "Whole city", label: "searched against your brief, not a partner list" },
      { value: "Minutes", label: "to a ranked London shortlist" },
      { value: "Same hour", label: "availability requested from every venue on it" },
      { value: "£0", label: "venue commission" },
    ],
    answer:
      "The fastest way to find a corporate venue in London is to let software do the first three days of the work. Comtev reads your brief, shortlists venues across London by capacity, area, layout and budget, requests availability from each one over WhatsApp, SMS, email or phone, negotiates the rate and confirms the booking for you. It is free to start, with plans from £79 a month, and it takes no commission from the venue, which is the opposite of how most London venue finders are paid.",
    stepsTitle: "How a London brief becomes a held date.",
    steps: [
      {
        title: "Tell it the London you mean.",
        body: "Zone 1 only, walkable from Liverpool Street, near a particular client, or anywhere with a decent late licence. Comtev takes the brief the way you would say it.",
      },
      {
        title: "Get a shortlist that knows London.",
        body: "Capacity fit, nearest stations, whether the room works for a plenary and breakouts, minimum spends, and the things that catch people out, such as load-in windows and the 11pm music cut-off.",
      },
      {
        title: "Dates asked for, replies handled.",
        body: "Every venue on the shortlist is asked for your dates in the same hour. Comtev chases the ones that go quiet, reads the replies back into the list and flags the holds.",
      },
      {
        title: "Confirm, then forget the inbox.",
        body: "Pick one. Comtev negotiates, confirms the booking with the venue and puts the cost into a budget your approver signs off. Christmas parties included.",
      },
    ],
    tableTitle: "A London venue-finding service, and Comtev.",
    tableHead: ["London venue-finding service", "Comtev"],
    rows: [
      { topic: "How they are paid", them: "Commission from the venue, or a venue subscription", us: "A monthly plan from you, free to start. The venue pays nothing" },
      { topic: "What is searched", them: "The venues on their books", us: "The open market, including venues no platform lists" },
      { topic: "Time to a shortlist", them: "A call, then one to three days", us: "Minutes" },
      { topic: "Availability", them: "Enquiries sent one at a time", us: "All venues asked at once, chased automatically" },
      { topic: "Negotiation and booking", them: "Varies by account manager", us: "Comtev negotiates and confirms; spend waits for your approver" },
      { topic: "Everything else", them: "Separate suppliers, separate emails", us: "Catering, AV, hotel rooms and flights priced into the same budget" },
    ],
    tableNote:
      "Hire Space, HeadBox and Tagvenue publish venue-side pricing on their own sites, as listing subscriptions or commission on bookings. None of that is wrong. It just means the venue, not the finder, is the paying customer.",
    featuresTitle: "Built for the events London teams actually run.",
    features: [
      {
        title: "Christmas party venues, sorted in October",
        body: "Searches by late licence, minimum spend per head, dance floor and private hire, asks for availability across the dates you can live with, and compares the per-head cost in one table.",
      },
      {
        title: "Conference venues that fit the agenda",
        body: "Plenary plus breakouts, AV in the room or brought in, a day delegate rate against room hire plus catering. Comtev prices both ways so you can see which is cheaper for your numbers.",
      },
      {
        title: "Travel built into the venue decision",
        body: "A venue near King's Cross can be cheaper overall than a cheaper venue in Canary Wharf once you price 60 people's journeys. Comtev shows the whole number.",
      },
      {
        title: "Hotels for the people coming in",
        body: "Room blocks compared and awarded under approval, next to the venue cost, for the attendees travelling into London.",
      },
      {
        title: "One budget, one approver",
        body: "Venue, catering, AV, rooms and travel in one live budget with scenarios. A frozen proposal goes to finance, and nothing is charged until they say yes.",
      },
    ],
    faq: [
      {
        q: "Which parts of London does the venue finder cover?",
        a: "All of it. Comtev searches the open market rather than a partner catalogue, so the City, Westminster, Shoreditch, King's Cross, Canary Wharf, the South Bank and the outer boroughs are all in scope. You can also constrain it to a postcode or a walk time from a station.",
      },
      {
        q: "Can it find a Christmas party venue in London at short notice?",
        a: "Yes, and short notice is where asking every venue at once matters most. Comtev requests availability from the whole shortlist in the same hour and flags who can still take your date.",
      },
      {
        q: "Does it work for conferences and all-hands as well as parties?",
        a: "Yes. Give it the agenda shape, such as plenary, breakouts and dinner, plus the headcount, and it shortlists spaces that fit, priced as a day delegate rate or as room hire plus catering so you can compare.",
      },
      {
        q: "How is this different from Hire Space or HeadBox?",
        a: "They are venue marketplaces, paid by venues through commission or subscriptions, and good at browsing. Comtev is paid by you, searches beyond any one catalogue, requests availability and negotiates for you, and puts the venue in the same budget as travel, catering and approvals.",
      },
      {
        q: "Will Comtev talk to the venue for me?",
        a: "Yes. Availability requests, follow-ups, negotiation and confirmation happen over WhatsApp, SMS, email or a call, and every message is logged on the event so you can read the thread without having been in it.",
      },
      {
        q: "What does it cost?",
        a: "Free to start, then plans from £79 a month, priced in pounds, whatever the venue costs. Enterprise plans exist for teams running many events a year.",
      },
    ],
    related: [
      { to: "/venue-finder", label: "The AI venue finder" },
      { to: "/corporate-event-planner", label: "The AI corporate event planner" },
      { to: "/use-cases/conferences", label: "Conferences and summits" },
      { to: "/corporate-event-cost-uk", label: "What UK corporate events cost" },
      { to: "/pricing", label: "Pricing" },
    ],
    ctaTitle: "Bring a London date to the demo.",
    ctaBody: "Tell us the date and the headcount. We will shortlist live and request availability before the call ends.",
    breadcrumb: "London",
    serviceName: "AI venue finding and booking for corporate events in London",
  },
  {
    slug: "corporate-event-planner",
    path: "/corporate-event-planner",
    title: "Corporate Event Planner UK: Hire the AI Instead | Comtev",
    description:
      "Looking for a corporate event planner in the UK? Comtev is the AI planner that finds venues, requests availability, negotiates suppliers, prices travel and builds the budget. Free to start, plans from £79 a month.",
    eyebrow: "Corporate event planner, UK",
    h1: "A corporate event planner that answers in minutes and never bills by the hour.",
    sub: "Comtev is the planner you brief at nine and hear back from at ten past. It finds the venue, asks suppliers for quotes, negotiates, prices the flights and rooms, and keeps one budget your finance team can sign. You keep every decision. It keeps every thread.",
    heroNote: "Free to start, plans from £79 a month. Built in the UK, priced in pounds.",
    stats: [
      { value: "One brief", label: "starts venues, suppliers, flights and rooms at the same time" },
      { value: "Minutes", label: "to the first shortlist" },
      { value: "Every message", label: "logged on the event, readable without being in the thread" },
      { value: "From £0", label: "free to start, then plans from £79 a month, whatever the budget" },
    ],
    answer:
      "A corporate event planner in the UK usually charges a day rate or a percentage of the event budget and starts with a discovery call. Comtev is an AI planner that starts with a typed brief, returns a ranked venue shortlist within minutes, requests availability, sources and negotiates supplier quotes over WhatsApp, SMS, email and voice, prices flights per named traveller and hotel room blocks, and rolls everything into one live budget with approvals. It is free to start, with plans from £79 a month, and it leaves every decision with you.",
    stepsTitle: "What a week with it looks like.",
    steps: [
      {
        title: "Brief it like a colleague.",
        body: "Kickoff for 140 in March, Barcelona or Lisbon, £100k all in, finance needs a proposal by Friday. That is enough to start.",
      },
      {
        title: "Watch the plan fill itself in.",
        body: "Venues, caterers, AV, photographers, hotels and flights are searched at once. Suppliers are contacted, chased and negotiated with. The event document fills up with options and open decisions.",
      },
      {
        title: "Decide from a short list, not a long inbox.",
        body: "Each option comes with the number, the terms and the trade-offs. Try a scenario, swap the venue, and the budget rebuilds.",
      },
      {
        title: "Send it for sign-off and stop chasing.",
        body: "A frozen proposal goes to the approver on Slack, push or email. Questions that stall the event are chased until answered. Nothing is booked or charged until the yes.",
      },
    ],
    tableTitle: "A freelance or agency planner, and Comtev.",
    tableHead: ["Freelance or agency planner", "Comtev"],
    rows: [
      { topic: "How you pay", them: "A day rate, or 10 to 15% of the budget", us: "A plan from £79 a month, free to start" },
      { topic: "Start time", them: "After a discovery call and a proposal", us: "The minute you type the brief" },
      { topic: "Venue search", them: "Their contacts and preferred venues", us: "The open market, ranked against your brief" },
      { topic: "Supplier quotes", them: "Requested by email, chased by a person", us: "Requested over four channels, chased and negotiated automatically" },
      { topic: "Travel", them: "Usually a separate travel agent", us: "Flights per named traveller and room blocks in the same budget, booked after approval" },
      { topic: "Stakeholder decisions", them: "Chased by you", us: "Chased by Comtev until answered" },
      { topic: "Who decides", them: "You, after the weekly call", us: "You, whenever you open the event" },
    ],
    tableNote:
      "The fee range comes from our UK cost guide, which names its sources. Good planners earn their fee on the day itself. Comtev is built for the weeks before it.",
    featuresTitle: "The parts of the job it takes off your desk.",
    features: [
      {
        title: "The event document",
        body: "One living page of what the event is and what still needs a decision. Items saved on the venue, supplier and travel boards land on it. Preview it, circulate a frozen snapshot, or put decided items straight into the budget.",
      },
      {
        title: "Suppliers contacted in their own language",
        body: "Quotes for an event abroad are requested and negotiated in the supplier's language, then shown to you in yours.",
      },
      {
        title: "A budget finance will actually sign",
        body: "Committed, forecast, headroom and variance, live. Versioned proposals with a per-guest figure and the caveats written in.",
      },
      {
        title: "Approvals with a ceiling",
        body: "Spend routes to a named approver with a limit and a 24-hour window. Approve from Slack, push or email.",
      },
      {
        title: "Guests who add themselves",
        body: "Team leads add names over Slack or WhatsApp with no app. Comtev chases stragglers and merges duplicates. QR passes on the day.",
      },
    ],
    faq: [
      {
        q: "Is Comtev a replacement for a corporate event planner?",
        a: "For the planning weeks, yes: sourcing, availability, quotes, negotiation, travel pricing, the budget and the approvals. On the day you still want a human running the room, and many teams use Comtev for the legwork and a freelancer or agency for on-site delivery.",
      },
      {
        q: "How much does a corporate event planner cost in the UK?",
        a: "Agencies and planners commonly charge a day rate or a percentage of the event budget, often in the 10 to 15% range for full-service work. Comtev is a monthly plan from £79, free to start, regardless of budget. The cost guide linked below sets out the published benchmarks.",
      },
      {
        q: "Can I use Comtev for a client's event, not just my own?",
        a: "Yes. The event document and the frozen proposal can be shared with a client, and the approval can be theirs.",
      },
      {
        q: "Which events is it good at?",
        a: "Company kickoffs, offsites, conferences and summits, all-hands, product launches, client dinners and Christmas parties. Anything where the work is finding, asking, chasing and budgeting.",
      },
      {
        q: "Does the AI make decisions for me?",
        a: "No. It proposes, prices and chases. You and your named approver decide, and nothing is booked or charged without that yes.",
      },
      {
        q: "What happens after I book a demo?",
        a: "Bring a real brief. We run it live, and you leave with a shortlist and a first-pass budget for that event.",
      },
    ],
    related: [
      { to: "/ai-event-planner-uk", label: "AI event planner for company offsites" },
      { to: "/event-management-company", label: "Instead of an event management company" },
      { to: "/venue-finder", label: "The AI venue finder" },
      { to: "/compare/ai-vs-event-management-company", label: "Agency or AI, compared" },
      { to: "/corporate-event-cost-uk", label: "What UK corporate events cost" },
      { to: "/pricing", label: "Pricing" },
    ],
    ctaTitle: "Bring the event you are dreading to the demo.",
    ctaBody: "The one with the inbox. We will brief Comtev together and watch the first shortlist come back.",
    breadcrumb: "Corporate event planner",
    serviceName: "AI corporate event planning for UK companies",
  },
  {
    slug: "event-management-company",
    path: "/event-management-company",
    title: "Event Management Company UK? See the AI Alternative | Comtev",
    description:
      "Before you hire a UK event management company, see what Comtev's AI does on a plan from £79 a month: venues found and booked, suppliers quoted and negotiated, travel priced, one budget, approvals handled.",
    eyebrow: "Event management company alternative",
    h1: "What an event management company does for 10 to 15% of your budget. From £79 a month.",
    sub: "An event management company brings contacts, a coordinator and an invoice that grows with your spend. Comtev brings the same legwork as software: venues found and held, suppliers quoted and negotiated over WhatsApp, SMS, email and voice, flights and rooms priced, one live budget and a sign-off your finance team can see. Keep the agency for the day itself, if you want one at all.",
    heroNote: "Free to start, plans from £79 a month. No percentage of anything.",
    stats: [
      { value: "£79", label: "a month for Starter, against 10 to 15% of the budget. Free to start" },
      { value: "Minutes", label: "from brief to venue shortlist" },
      { value: "4 channels", label: "for supplier comms: WhatsApp, SMS, email and voice" },
      { value: "1 budget", label: "venue, suppliers, travel and approvals together" },
    ],
    answer:
      "A UK event management company plans and delivers corporate events for a fee that is usually a percentage of the budget or a day rate, so a £100,000 event can carry £10,000 to £15,000 in management fees before anything is booked. Comtev is the AI alternative for the planning phase. It finds and holds the venue, requests and negotiates supplier quotes, prices travel per named traveller, builds the budget and routes spend to a named approver, on a plan from £79 a month. Teams that want on-site delivery still hire it, for the day rather than the months.",
    stepsTitle: "The agency's first fortnight, back in an hour.",
    steps: [
      {
        title: "Brief Comtev instead of writing an RFP.",
        body: "Type what you would have told the agency. Comtev asks the questions a good account manager would, then starts.",
      },
      {
        title: "Options arrive the same day.",
        body: "Venue shortlist ranked and availability requested. Caterers, AV and photographers contacted. Flights and rooms priced from your attendees' cities.",
      },
      {
        title: "See the negotiated numbers, not a mark-up.",
        body: "Comtev pushes suppliers on rate and terms and shows you the result. There is no margin on a supplier's price because Comtev takes no commission.",
      },
      {
        title: "Approve and deliver.",
        body: "Freeze a proposal, get the yes, confirm the bookings. Bring in an on-site team for the day if the event needs one. They get a finished plan, not a brief.",
      },
    ],
    tableTitle: "An event management company, and Comtev, side by side.",
    tableHead: ["Event management company", "Comtev"],
    rows: [
      { topic: "Fee", them: "10 to 15% of the budget, or a day rate", us: "A plan from £79 a month, free to start" },
      { topic: "Time to first options", them: "Discovery call, proposal, then one to two weeks", us: "Minutes" },
      { topic: "Venue sourcing", them: "Preferred venues and relationships", us: "Open-market search, ranked to your brief, no commission" },
      { topic: "Supplier quotes", them: "Requested and chased by a coordinator", us: "Requested over four channels, chased and negotiated automatically, compared side by side" },
      { topic: "Travel and rooms", them: "Often a separate travel company", us: "Flights per traveller and room blocks in the same budget, booked after approval" },
      { topic: "Budget visibility", them: "A spreadsheet at the weekly check-in", us: "Live: committed, forecast, headroom and variance" },
      { topic: "Approvals", them: "An email thread", us: "A named approver with a ceiling, on Slack or email, logged on the event" },
      { topic: "On the day", them: "Their strength", us: "QR passes and door-staff check-in. Bring a human for the room" },
    ],
    tableNote:
      "Fee ranges come from the UK cost guide linked below, which names its sources. If your agency charges differently, use their number. The comparison still holds.",
    featuresTitle: "What changes when the legwork is software.",
    features: [
      {
        title: "No RFP, no pitch deck, no waiting",
        body: "The brief is the RFP. Options start arriving the same day, and you can change the brief without starting a new procurement.",
      },
      {
        title: "Comms you can read without being in them",
        body: "Every supplier conversation sits on the event, by channel and by supplier. There is an audit trail, which procurement will like.",
      },
      {
        title: "Scenarios instead of revised proposals",
        body: "Finance wants a leaner version? Build a scenario, swap the venue, and the budget rebuilds. Send both.",
      },
      {
        title: "Stakeholders chased for you",
        body: "Approvals, questions, content requests and date holds go out over Slack and email, get chased, get re-routed to a delegate and escalate. You see who the bottleneck is.",
      },
      {
        title: "Automation for the recurring events",
        body: "Quarterly all-hands, monthly client dinners: set a flow once and Comtev runs the follow-ups, with approval before anything spends money.",
      },
    ],
    faq: [
      {
        q: "Is Comtev an event management company?",
        a: "No. It is software with an AI that does the planning legwork an agency would bill for. It does not send staff to your event. Many teams pair it with an on-site crew for delivery.",
      },
      {
        q: "How much does an event management company charge in the UK?",
        a: "Published benchmarks put full-service agency fees in the region of 10 to 15% of the event budget, or a day rate for smaller jobs. Our cost guide lists the sources.",
      },
      {
        q: "What can't it do that an agency can?",
        a: "Run the room on the day, manage a crew, or carry the liability for production. It also does not have a decade of personal relationships with a particular venue, although it will ask that venue the same questions in minutes.",
      },
      {
        q: "Can an agency use Comtev too?",
        a: "Yes. Agencies and freelance planners use it to do the sourcing and budgeting for their clients faster, and share the frozen proposal with the client for sign-off.",
      },
      {
        q: "Is anything booked without my approval?",
        a: "No. Spend routes to a named approver with a ceiling. Flights and hotel rooms are booked in-app after the yes. Venue and supplier bookings are confirmed with the supplier on your behalf after the yes.",
      },
      {
        q: "What does a plan include?",
        a: "Everything on this page: venue sourcing and booking, supplier quotes and negotiation, travel pricing, the budget, approvals, stakeholder chasing, the guest list and check-in. The free plan covers one active event. Starter, Team and Business add AI credits, active events and team members, and Enterprise is on request.",
      },
    ],
    related: [
      { to: "/compare/ai-vs-event-management-company", label: "Agency or AI, compared in detail" },
      { to: "/corporate-event-planner", label: "The AI corporate event planner" },
      { to: "/venue-finder", label: "The AI venue finder" },
      { to: "/corporate-event-cost-uk", label: "What UK corporate events cost" },
      { to: "/products/quotes", label: "Supplier quotes and negotiation" },
      { to: "/pricing", label: "Pricing" },
    ],
    ctaTitle: "Send us the brief you were going to send the agency.",
    ctaBody: "We will run it live on the demo. You will see the first shortlist and the first negotiated quote before you decide anything.",
    breadcrumb: "Event management company alternative",
    serviceName: "AI alternative to an event management company for UK corporate events",
  },
];

export const COMPETE_PATHS: string[] = COMPETE_PAGES.map((p) => p.path);

export function getCompetePage(slug: string): CompetePage | undefined {
  return COMPETE_PAGES.find((p) => p.slug === slug);
}
